import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DataTable,
  Field,
  Input,
  Modal,
  StateBlock,
  useToast,
  type Column,
} from '@w7/ui'
import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { authHeaders } from '@/auth/session'
import { CmPanel } from '@/features/catalog/CmPanel'

type ListResult<T> = {
  ok: true
  total: number
  page: number
  pageSize: number
  rows: T[]
}

export function SecurityCrudPage<T extends { id: string }>({
  title,
  subtitle,
  queryKey,
  listFn,
  deleteFn,
  deleteConfirm,
  columns,
  form,
  emptyHint,
}: {
  title: string
  subtitle: string
  queryKey: string
  listFn: (
    headers: HeadersInit,
    body: { page: number; pageSize: number; q?: string },
  ) => Promise<ListResult<T>>
  deleteFn?: (headers: HeadersInit, body: { id: string }) => Promise<{ ok: true; deleted: number }>
  deleteConfirm?: string
  columns: { key: string; header: string; cell: (row: T) => ReactNode }[]
  form?: ReactNode
  emptyHint: string
}) {
  const toast = useToast()
  const qc = useQueryClient()
  const [page, setPage] = useState(1)
  const [q, setQ] = useState('')
  const [qDraft, setQDraft] = useState('')
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const pageSize = 50

  const listQ = useQuery({
    queryKey: ['cm', 'security', queryKey, page, pageSize, q],
    queryFn: () => listFn(authHeaders(), { page, pageSize, q: q || undefined }),
  })

  const delM = useMutation({
    mutationFn: (id: string) => {
      if (!deleteFn) throw new Error('delete not supported')
      return deleteFn(authHeaders(), { id })
    },
    onSuccess: async () => {
      toast.success('Deleted')
      setPendingDeleteId(null)
      await qc.invalidateQueries({ queryKey: ['cm', 'security', queryKey] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : String(err)),
  })

  const rows = listQ.data?.rows ?? []
  const total = listQ.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const confirmMsg = deleteConfirm ?? 'Delete this row?'

  const tableColumns = useMemo((): Column<T>[] => {
    const base: Column<T>[] = [
      {
        id: 'n',
        header: '#',
        width: 48,
        numeric: true,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      ...columns.map((c) => ({
        id: c.key,
        header: c.header,
        cell: (row: T) => c.cell(row),
      })),
    ]
    if (deleteFn) {
      base.push({
        id: 'actions',
        header: 'Actions',
        cell: (row) => (
          <Button
            type="button"
            size="sm"
            variant="danger"
            busy={delM.isPending && pendingDeleteId === row.id}
            onClick={() => setPendingDeleteId(row.id)}
          >
            Delete
          </Button>
        ),
      })
    }
    return base
  }, [columns, deleteFn, delM.isPending, page, pageSize, pendingDeleteId])

  if (listQ.isLoading && !rows.length) {
    return (
      <StateBlock icon="settings" title={`Loading ${title}…`}>
        Reading CM-API security lists.
      </StateBlock>
    )
  }

  return (
    <CmPanel
      title={title}
      subtitle={subtitle}
      actions={
        <Button
          type="button"
          variant="secondary"
          busy={listQ.isFetching}
          onClick={() => void listQ.refetch()}
        >
          Refresh
        </Button>
      }
    >
      <div className="cm-security">
        <form
          className="cm-toolbar"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            setPage(1)
            setQ(qDraft.trim())
          }}
        >
          <div className="cm-toolbar-search">
            <Field label="Search">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={qDraft}
                  onChange={(e) => setQDraft(e.target.value)}
                  placeholder="Search…"
                />
              )}
            </Field>
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        {form ? <div className="cm-security-form">{form}</div> : null}

        {listQ.isError ? (
          <div className="cm-hint" role="alert">
            <p>{(listQ.error as Error).message}</p>
            <Button type="button" variant="secondary" onClick={() => void listQ.refetch()}>
              Retry
            </Button>
          </div>
        ) : null}

        <DataTable
          label={`${title} list`}
          density="compact"
          rows={rows}
          columns={tableColumns}
          rowKey={(row) => row.id}
          loading={listQ.isFetching && !rows.length}
          empty={
            !listQ.isError ? (
              <div className="cm-security-empty">
                <StateBlock icon="search" title="Nothing here yet">
                  {emptyHint}
                </StateBlock>
              </div>
            ) : null
          }
        />

        {total > pageSize ? (
          <div className="cm-security-pager">
            <Button
              type="button"
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="cm-hint">
              Page {page} / {totalPages} · {total} row(s)
            </span>
            <Button
              type="button"
              variant="secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </div>

      {deleteFn ? (
        <Modal
          open={pendingDeleteId != null}
          onClose={() => {
            if (delM.isPending) return
            setPendingDeleteId(null)
          }}
          title="Confirm delete"
          subtitle={confirmMsg}
          tone="danger"
          closeDisabled={delM.isPending}
          footer={
            <div className="cm-form-actions">
              <Button
                type="button"
                variant="secondary"
                disabled={delM.isPending}
                onClick={() => setPendingDeleteId(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                busy={delM.isPending}
                onClick={() => {
                  if (pendingDeleteId) delM.mutate(pendingDeleteId)
                }}
              >
                Delete
              </Button>
            </div>
          }
        >
          <p className="cm-hint">This action cannot be undone from the console.</p>
        </Modal>
      ) : null}
    </CmPanel>
  )
}
